import type { ChronologyResolution } from './chronology.js';
import type { NormalizedChart } from './domain.js';
import type { GeneratedNatalReport } from './interpretation.js';
import { buildCalculationContextViewModel } from './calculationContextPresentation.js';
import { topicInterpretationViewModel } from './presentation.js';
import type { ResultSummaryViewModel } from './resultSummary.js';
import type { TopicCoverageViewModel } from './topicCoveragePresentation.js';
import { buildOverviewDigest } from './overviewDigest.js';
import { buildResultActionsViewModel } from './resultActionsPresentation.js';
import { buildClaimExplanationPacket, type ClaimExplanationPacket } from './explainability.js';
import { extractNatalFeatures } from './featureExtractor.js';
import { buildTopicSyntheses, type TopicSynthesisViewModel } from './topicSynthesis.js';
import { buildTopicStructuralContexts, type TopicStructuralContextViewModel } from './topicStructuralContext.js';
import { buildPalaceNarratives, type PalaceNarrativeViewModel } from './palaceNarrative.js';
import { buildFullChartSynthesis, type FullChartSynthesisViewModel } from './fullChartSynthesis.js';
import { buildOverviewReaderDepth, type OverviewReaderDepthViewModel } from './overviewReaderDepth.js';

export interface ProductionNatalResultPageViewModel {
  model: 'PRODUCTION-NATAL-RESULT-PAGE-0.1.0';
  calculation: ReturnType<typeof buildCalculationContextViewModel>;
  interpretationProvenance?: Readonly<{ school: string; sourceRegistry: string; rulePack: string; templatePack: string }>;
  summary: ResultSummaryViewModel;
  overviewDigest: ReturnType<typeof buildOverviewDigest>;
  overviewReaderDepth: OverviewReaderDepthViewModel;
  actions: ReturnType<typeof buildResultActionsViewModel>;
  topicCoverage: TopicCoverageViewModel;
  topics: readonly ReturnType<typeof topicInterpretationViewModel>[];
  claimExplanations: readonly ClaimExplanationPacket[];
  topicSyntheses: readonly TopicSynthesisViewModel[];
  topicStructuralContexts: readonly TopicStructuralContextViewModel[];
  palaceNarratives: readonly PalaceNarrativeViewModel[];
  fullChartSynthesis: FullChartSynthesisViewModel;
  privacyNotice: string;
  culturalDisclosure: string;
}

export function buildProductionNatalResultPageViewModel(input: {
  chart: NormalizedChart;
  report: GeneratedNatalReport;
  summary: ResultSummaryViewModel;
  overviewDigest: ReturnType<typeof buildOverviewDigest>;
  actions: ReturnType<typeof buildResultActionsViewModel>;
  topicCoverage: TopicCoverageViewModel;
  chronology?: Readonly<{ birth: ChronologyResolution; target?: ChronologyResolution }>;
}): ProductionNatalResultPageViewModel {
  const reportTopics = input.report.topics.map((item) => item.topic);
  const coverageTopics = input.topicCoverage.items.map((item) => item.topic);
  if (reportTopics.join('|') !== coverageTopics.join('|')) {
    throw new Error('Production result page rejected report/topic-coverage ordering mismatch');
  }
  if (input.report.versions.calculationRuleset !== `${input.chart.calculation.rulesetId}@${input.chart.calculation.rulesetVersion}`) {
    throw new Error('Production result page rejected chart/report calculation-ruleset mismatch');
  }
  for (const topic of input.report.topics) {
    if (topic.versions.interpretationRulePack !== input.report.versions.interpretationRulePack
      || topic.versions.templatePack !== input.report.versions.templatePack
      || topic.versions.interpretationSchool !== input.report.versions.interpretationSchool
      || topic.versions.sourceRegistry !== input.report.versions.sourceRegistry) {
      throw new Error('Production result page rejected mixed interpretation provenance');
    }
  }

  const features = extractNatalFeatures(input.chart);
  const claims = input.report.topics.flatMap((topic) => topic.claims);
  const topicViews = Object.freeze(input.report.topics.map(topicInterpretationViewModel));
  const claimExplanations = Object.freeze(claims.map((claim) => buildClaimExplanationPacket(claim, features)));
  const topicSyntheses = buildTopicSyntheses(topicViews, claimExplanations);
  const fullChartSynthesis = buildFullChartSynthesis(topicViews, topicSyntheses);
  const overviewDigest = buildOverviewDigest(input.report);
  const overviewReaderDepth = buildOverviewReaderDepth(overviewDigest, topicSyntheses, fullChartSynthesis);
  return Object.freeze({
    model: 'PRODUCTION-NATAL-RESULT-PAGE-0.1.0',
    calculation: buildCalculationContextViewModel(input.chart, input.chronology),
    ...(input.report.versions.interpretationSchool ? {
      interpretationProvenance: Object.freeze({
        school: input.report.versions.interpretationSchool,
        sourceRegistry: input.report.versions.sourceRegistry,
        rulePack: input.report.versions.interpretationRulePack,
        templatePack: input.report.versions.templatePack,
      }),
    } : {}),
    summary: input.summary,
    overviewDigest,
    overviewReaderDepth,
    actions: buildResultActionsViewModel(),
    topicCoverage: input.topicCoverage,
    topics: topicViews,
    claimExplanations,
    topicSyntheses,
    topicStructuralContexts: buildTopicStructuralContexts(input.chart, topicViews.map((item)=>item.topic)),
    palaceNarratives: buildPalaceNarratives(input.chart),
    fullChartSynthesis,
    privacyNotice: '出生資料與命盤預設只在目前瀏覽器本機處理；本結果頁不需要姓名、Email、電話或精確地址。',
    culturalDisclosure: '本頁內容是傳統紫微斗數的規則式文化解讀與自我探索資訊，不是科學已證實的預測。',
  });
}
